import { useEffect, useMemo, useRef, useState } from "react";
import consts from "../constant";

const RECONNECT_DELAY_MS = 1700;
const REST_POLL_INTERVAL_MS = 4500;
const BINANCE_SPOT_WS = "wss://stream.binance.com:9443/stream";
const BINANCE_FUTURES_WS = "wss://fstream.binance.com/stream";
const BINANCE_SPOT_REST = "https://api.binance.com/api/v3/ticker/24hr";
const BINANCE_FUTURES_REST = "https://fapi.binance.com/fapi/v1/ticker/24hr";

async function fetchWithTimeout(url, { signal, timeoutMs = 3000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => {
    try { controller.abort(); } catch (_) {}
  }, timeoutMs);

  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => { try { controller.abort(); } catch (_) {} }, { once: true });
  }

  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

const sharedManagers = new Map();

class TickerStreamManager {
  constructor(socketUrl, restEndpoint) {
    this.socketUrl = socketUrl;
    this.restEndpoint = restEndpoint;
    this.socket = null;
    this.subscribers = new Set();
    this.reconnectTimer = null;
    this.restPollIntervalId = null;
    this.isCancelled = false;
    this.isRestPollInFlight = false;
    this.latestDataBySymbol = new Map();
  }

  addSubscriber(applyTickerUpdate, startPairs) {
    const subscriber = { applyTickerUpdate };
    this.subscribers.add(subscriber);

    for (const [symbol, update] of this.latestDataBySymbol.entries()) {
      subscriber.applyTickerUpdate(symbol, update);
    }

    if (this.subscribers.size === 1) {
      this.isCancelled = false;
      this.connect();
      this.pollTickerSnapshot(startPairs);
      // this.restPollIntervalId = setInterval(() => this.pollTickerSnapshot(startPairs), REST_POLL_INTERVAL_MS);
    }

    return () => {
      this.subscribers.delete(subscriber);
      if (this.subscribers.size === 0) {
        this.stop();
        sharedManagers.delete(this.socketUrl);
      }
    };
  }

  broadcastUpdate(symbol, update) {
    let existing = this.latestDataBySymbol.get(symbol);
    if (!existing) {
      existing = {};
      this.latestDataBySymbol.set(symbol, existing);
    }
    Object.assign(existing, update);

    for (const sub of this.subscribers) {
      sub.applyTickerUpdate(symbol, update);
    }
  }

  scheduleReconnect() {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectTimer = setTimeout(() => {
      if (!this.isCancelled) {
        this.connect();
      }
    }, RECONNECT_DELAY_MS);
  }

  connect() {
    if (this.isCancelled) return;
    this.socket = new WebSocket(this.socketUrl);
    this.socket.onmessage = (event) => {
      const payload = safeJsonParse(event.data);
      const data = payload?.data;
      if (!data || typeof data.s !== "string") {
        return;
      }

      const nextPrice = toFiniteNumber(data.c);
      const nextOpenPrice = toFiniteNumber(data.o);
      const nextHighPrice = toFiniteNumber(data.h);
      const nextLowPrice = toFiniteNumber(data.l);
      const rawChangePercent = toFiniteNumber(data.P);
      const nextChangePercent = Number.isFinite(rawChangePercent)
        ? rawChangePercent
        : deriveChangePercent(nextPrice, nextOpenPrice, NaN);

      this.broadcastUpdate(data.s, {
        price: nextPrice,
        openPrice: nextOpenPrice,
        highPrice: nextHighPrice,
        lowPrice: nextLowPrice,
        changePercent: nextChangePercent,
      });
    };
    this.socket.onerror = () => {
      this.socket?.close();
    };
    this.socket.onclose = () => {
      if (!this.isCancelled) {
        this.scheduleReconnect();
      }
    };
  }

  async pollTickerSnapshot(pairs) {
    if (this.isCancelled || this.isRestPollInFlight) {
      return;
    }

    this.isRestPollInFlight = true;
    try {
      const symbols = pairs.map((pair) => pair.symbol);
      const encodedSymbols = encodeURIComponent(JSON.stringify(symbols));
      const sep = this.restEndpoint.includes("?") ? "&" : "?";
      const response = await fetchWithTimeout(
        `${this.restEndpoint}${sep}symbols=${encodedSymbols}`,
        { timeoutMs: 3500 }
      );
      if (!response.ok) {
        return;
      }

      const payload = await response.json();
      const tickers = Array.isArray(payload) ? payload : [];
      for (const ticker of tickers) {
        if (typeof ticker?.symbol !== "string") {
          continue;
        }

        const nextPrice = toFiniteNumber(ticker.lastPrice);
        const nextOpenPrice = toFiniteNumber(ticker.openPrice);
        const nextHighPrice = toFiniteNumber(ticker.highPrice);
        const nextLowPrice = toFiniteNumber(ticker.lowPrice);
        const nextChangePercent = toFiniteNumber(ticker.priceChangePercent);

        this.broadcastUpdate(ticker.symbol, {
          price: nextPrice,
          openPrice: nextOpenPrice,
          highPrice: nextHighPrice,
          lowPrice: nextLowPrice,
          changePercent: nextChangePercent,
        });
      }
    } catch (_error) {
    } finally {
      this.isRestPollInFlight = false;
    }
  }

  stop() {
    this.isCancelled = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.restPollIntervalId !== null) {
      clearInterval(this.restPollIntervalId);
      this.restPollIntervalId = null;
    }
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      this.socket.close();
    }
    this.socket = null;
  }
}

export function useBinancePairsTicker(pairs, pairType = "crypto") {

  // 🔧 FIX: Stabilize safePairs by keying the memo on the serialized symbol
  //         list rather than the array reference. This prevents re-subscriptions
  //         when the parent re-renders and passes a new array with identical
  //         content (e.g. after any unrelated state update in the parent).
  const symbolKey = Array.isArray(pairs) ? pairs.map((p) => p.symbol).join(',') : '';
  const safePairs = useMemo(
    () => (Array.isArray(pairs) ? pairs : []),
    [symbolKey]
  );

  const [statsBySymbol, setStatsBySymbol] = useState(() =>
    buildInitialStatsMap(safePairs),
  );
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    setStatsBySymbol((previous) => reconcileStatsMap(previous, safePairs));
  }, [symbolKey]); // 🔧 FIX: depend on stable key, not the array reference

  useEffect(() => {
    if (safePairs.length === 0) {
      return undefined;
    }

    const isFutures = pairType === "future";
    const streams = safePairs
      .map((pair) => `${pair.symbol.toLowerCase()}@ticker`)
      .join("/");

    let binanceSocketUrl = `${isFutures ? BINANCE_FUTURES_WS : BINANCE_SPOT_WS}?streams=${streams}`;
    let restEndpoint = isFutures ? BINANCE_FUTURES_REST : BINANCE_SPOT_REST;

    if (consts?.socketUrl) {
      const wsBase = String(consts.socketUrl).replace(/^http/, "ws").replace(/\/$/, "");
      binanceSocketUrl = `${wsBase}/binance-feed?streams=${streams}`;
    }
    if (consts?.BackendUrl) {
      restEndpoint = `${consts.BackendUrl}/binance/ticker/24hr?type=${isFutures ? "futures" : "spot"}`;
    }

    let manager = sharedManagers.get(binanceSocketUrl);
    if (!manager) {
      manager = new TickerStreamManager(binanceSocketUrl, restEndpoint);
      sharedManagers.set(binanceSocketUrl, manager);
    }

    const applyTickerUpdate = (symbol, update) => {
      const hasAnyUpdate =
        Number.isFinite(update.price) ||
        Number.isFinite(update.openPrice) ||
        Number.isFinite(update.highPrice) ||
        Number.isFinite(update.lowPrice) ||
        Number.isFinite(update.changePercent);
      if (!hasAnyUpdate) {
        return;
      }

      setStatsBySymbol((previous) => {
        const existing = previous[symbol];
        if (!existing) {
          return previous;
        }

        const nextPrice = firstFinite(
          update.price,
          existing.price,
          existing.fallbackPrice,
        );
        const nextOpenPrice = firstFinite(
          update.openPrice,
          existing.openPrice,
          nextPrice,
        );
        const nextHighPrice = firstFinite(
          update.highPrice,
          existing.highPrice,
          nextPrice,
        );
        const nextLowPrice = firstFinite(
          update.lowPrice,
          existing.lowPrice,
          nextPrice,
        );
        const nextChangePercent = Number.isFinite(update.changePercent)
          ? update.changePercent
          : deriveChangePercent(
            nextPrice,
            nextOpenPrice,
            existing.changePercent,
          );

        // 🔧 FIX: Guard against identical values — if nothing actually changed,
        //         return the previous reference so React skips the re-render.
        //         This is critical for high-frequency socket streams where ticks
        //         may carry the same price repeatedly (e.g., flat markets).
        const finalChangePercent = Number.isFinite(nextChangePercent)
          ? nextChangePercent
          : existing.changePercent;
        if (
          existing.price === nextPrice &&
          existing.openPrice === nextOpenPrice &&
          existing.highPrice === nextHighPrice &&
          existing.lowPrice === nextLowPrice &&
          existing.changePercent === finalChangePercent
        ) {
          return previous; // 🔧 FIX: stable reference → React bails out of re-render
        }

        return {
          ...previous,
          [symbol]: {
            ...existing,
            price: nextPrice,
            openPrice: nextOpenPrice,
            highPrice: nextHighPrice,
            lowPrice: nextLowPrice,
            spread: calculateSpread(nextHighPrice, nextLowPrice),
            spreadPercent: calculateSpreadPercent(nextHighPrice, nextLowPrice),
            changePercent: finalChangePercent,
            direction: resolveDirection(
              nextPrice,
              nextOpenPrice,
              nextChangePercent,
            ),
            isLive: true,
          },
        };
      });
    };

    return manager.addSubscriber(applyTickerUpdate, safePairs);
  }, [symbolKey, pairType]); // 🔧 FIX: depend on stable symbolKey, not the safePairs reference

  return useMemo(
    () =>
      safePairs.map((pair) => statsBySymbol[pair.symbol] ?? createPairStats(pair)),
    [safePairs, statsBySymbol],
  );
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
}

function buildInitialStatsMap(pairs) {
  const nextMap = {};
  if (!Array.isArray(pairs)) {
    return nextMap;
  }
  for (const pair of pairs) {
    nextMap[pair.symbol] = createPairStats(pair);
  }
  return nextMap;
}

function reconcileStatsMap(previous, pairs) {
  const nextMap = {};
  if (!Array.isArray(pairs)) {
    return nextMap;
  }

  for (const pair of pairs) {
    const existing = previous[pair.symbol];
    nextMap[pair.symbol] = createPairStats(pair, existing);
  }

  return nextMap;
}

function createPairStats(pair, existing = null) {

  const price = firstFinite(existing?.price, pair.fallbackPrice);
  const openPrice = firstFinite(existing?.openPrice, pair.fallbackPrice, price);
  const highPrice = firstFinite(existing?.highPrice, pair.fallbackPrice, price);
  const lowPrice = firstFinite(existing?.lowPrice, pair.fallbackPrice, price);
  const changePercent = Number.isFinite(existing?.changePercent)
    ? existing.changePercent
    : deriveChangePercent(price, openPrice, 0);

  return {
    symbol: pair.symbol,
    label: pair.label,
    icon: pair.icon || existing?.icon || "",
    fallbackPrice: pair.fallbackPrice,
    price,
    openPrice,
    highPrice,
    lowPrice,
    spread: calculateSpread(highPrice, lowPrice),
    spreadPercent: calculateSpreadPercent(highPrice, lowPrice),
    changePercent: Number.isFinite(changePercent) ? changePercent : 0,
    direction: resolveDirection(price, openPrice, changePercent),
    isLive: Boolean(existing?.isLive),
  };
}

function deriveChangePercent(price, openPrice, fallbackValue = NaN) {
  if (Number.isFinite(price) && Number.isFinite(openPrice) && openPrice !== 0) {
    return ((price - openPrice) / openPrice) * 100;
  }
  return Number.isFinite(fallbackValue) ? fallbackValue : 0;
}

function calculateSpread(highPrice, lowPrice) {
  if (!Number.isFinite(highPrice) || !Number.isFinite(lowPrice)) {
    return 0;
  }
  return Math.max(0, highPrice - lowPrice);
}

function calculateSpreadPercent(highPrice, lowPrice) {
  if (!Number.isFinite(lowPrice) || lowPrice <= 0) {
    return 0;
  }
  return (calculateSpread(highPrice, lowPrice) / lowPrice) * 100;
}

function resolveDirection(price, openPrice, changePercent) {
  if (
    Number.isFinite(price) &&
    Number.isFinite(openPrice) &&
    price !== openPrice
  ) {
    return price > openPrice ? "up" : "down";
  }

  if (Number.isFinite(changePercent) && changePercent !== 0) {
    return changePercent > 0 ? "up" : "down";
  }

  return "flat";
}

function firstFinite(...values) {
  for (const value of values) {
    if (Number.isFinite(value)) {
      return value;
    }
  }
  return NaN;
}

function toFiniteNumber(value) {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : NaN;
}
